import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './EnginePage.css';

const CONVICTION_COLORS = {
  new: '#b5682a',
  strengthening: '#b5682a',
  weakening: '#7a6f5f',
  stable: '#7a6f5f',
  resolved: '#a69e8e',
};

const STATUS_LABELS = {
  active: { bg: '#f2efe9', color: '#b5682a' },
  fading: { bg: '#f2efe9', color: '#7a6f5f' },
  resolved: { bg: '#f2efe9', color: '#a69e8e' },
};

export default function EnginePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/engine')
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="engine-page"><div className="engine-loading">Loading engine data...</div></div>;
  if (!data) return <div className="engine-page"><div className="engine-loading">Engine data unavailable.</div></div>;

  const { constants, ledger, manifest, history } = data;
  const kw = constants?.constants?.keywords || {};
  const analysts = constants?.constants?.analysts?.registered || {};
  const signals = ledger?.signals || [];
  const inputs = manifest?.inputs || {};
  const outputs = manifest?.outputs || {};

  return (
    <div className="engine-page">
      <Link to="/markets" className="engine-back">&larr; Back to Market Daily Brief</Link>

      {/* Hero */}
      <header className="engine-hero">
        <h1 className="engine-title">Signal Engine</h1>
        <p className="engine-subtitle">
          How the Market Daily Brief is generated, step by step.
          Every claim is cited. Every input is logged.
        </p>
        <pre className="engine-equation">
{`thesis(t) = synthesize(constants(t), variables(t), history(t-1...))

constants  = news + jobs + twitter(preset kw) + FRED + prices
variables  = analyst notes → keyword extraction → widens twitter/polymarket search
history    = signal ledger (named signals tracked across runs, conviction over time)`}
        </pre>
      </header>

      {/* ── TURN 0 ─────────────────────────────────────────────────────── */}
      <section className="engine-section">
        <div className="engine-turn-header">
          <span className="engine-turn-badge">Turn 0</span>
          <h2 className="engine-section-title">Load Constants + Freshness Checks</h2>
        </div>
        <p className="engine-how">
          Load <code>thesis-constants.yaml</code> — the static configuration that defines
          what the engine searches for. Check if Polymarket snapshot and news database
          are fresh ({"<"}6h old). If stale, trigger upstream pipelines before proceeding.
        </p>

        <h3 className="engine-subsection">Always Keywords</h3>
        <p className="engine-detail">Searched across all sources every run.</p>
        <div className="engine-pills">
          {(kw.always || []).map(k => <span key={k} className="engine-pill">{k}</span>)}
        </div>

        {kw.twitter_only?.length > 0 && (
          <>
            <h3 className="engine-subsection">Twitter-Only Keywords</h3>
            <p className="engine-detail">Additional terms searched only on Twitter/X.</p>
            <div className="engine-pills">
              {kw.twitter_only.map(k => <span key={k} className="engine-pill engine-pill--twitter">{k}</span>)}
            </div>
          </>
        )}

        {kw.polymarket_only?.length > 0 && (
          <>
            <h3 className="engine-subsection">Polymarket-Only Keywords</h3>
            <p className="engine-detail">Additional terms searched only on Polymarket via Gamma API.</p>
            <div className="engine-pills">
              {kw.polymarket_only.map(k => <span key={k} className="engine-pill engine-pill--poly">{k}</span>)}
            </div>
          </>
        )}

        <h3 className="engine-subsection">Registered Analysts</h3>
        <p className="engine-detail">
          Notes from these analysts are loaded from the vault. Their content is never
          injected into the synthesis prompt — only keywords are extracted to widen the search aperture.
        </p>
        <div className="engine-analysts">
          {Object.entries(analysts).map(([key, val]) => (
            <div key={key} className="engine-analyst-card">
              <span className="engine-analyst-name">{val.display_name}</span>
              <span className="engine-analyst-prefix">File prefixes: {val.file_prefixes?.join(', ')}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── TURN 1 ─────────────────────────────────────────────────────── */}
      <section className="engine-section">
        <div className="engine-turn-header">
          <span className="engine-turn-badge">Turn 1</span>
          <h2 className="engine-section-title">Constants Pull — Fetch Static Data</h2>
        </div>
        <p className="engine-how">
          Pull all constant data sources in parallel. These are the same structure every run — only the data changes.
        </p>

        <div className="engine-step-grid">
          <div className="engine-step-card">
            <div className="engine-step-name">News Headlines</div>
            <div className="engine-step-source">News-Clients PostgreSQL</div>
            <p className="engine-step-desc">
              24h lookback, max 500 articles. Topic-balanced distribution: markets/credit 30%,
              energy/geopolitics 25%, tech/AI/labor 20%, health 10%, world 8%, keyword fallback 7%.
            </p>
            {inputs.news && <div className="engine-step-stat">{inputs.news.count} articles pulled</div>}
          </div>

          <div className="engine-step-card">
            <div className="engine-step-name">Entity Signals</div>
            <div className="engine-step-source">News-Clients entity tables</div>
            <p className="engine-step-desc">
              Top companies and industries by mention count. Private credit names
              (Blue Owl, Ares, KKR, etc.) tracked separately as a standalone signal layer.
            </p>
            {inputs.entities && (
              <div className="engine-step-stat">
                {inputs.entities.companies} companies, {inputs.entities.private_credit || 0} private credit names
              </div>
            )}
          </div>

          <div className="engine-step-card">
            <div className="engine-step-name">Job Signals</div>
            <div className="engine-step-source">CareersPageFinder DB</div>
            <p className="engine-step-desc">
              Hiring velocity by industry and job title. Leading indicator that
              lags public guidance by 4-8 weeks. Top roles with sample companies.
            </p>
            {inputs.jobs && <div className="engine-step-stat">{inputs.jobs.count} job signals</div>}
          </div>

          <div className="engine-step-card">
            <div className="engine-step-name">Macro Data</div>
            <div className="engine-step-source">FRED via latest.json</div>
            <p className="engine-step-desc">
              Fed Funds Rate, PCE, GDP, unemployment, yields, credit spreads,
              breakeven inflation, VIX. Each series with latest value and date.
            </p>
            {inputs.fred && <div className="engine-step-stat">{inputs.fred.series_count} FRED series</div>}
          </div>
        </div>
      </section>

      {/* ── TURN 1.5 ───────────────────────────────────────────────────── */}
      <section className="engine-section">
        <div className="engine-turn-header">
          <span className="engine-turn-badge">Turn 1.5</span>
          <h2 className="engine-section-title">Extract Dynamic Keywords</h2>
        </div>
        <p className="engine-how">
          Pure Python keyword extraction from the news pull (no LLM). Top companies by mention,
          proper nouns from headlines, private credit names boosted 2x. These expand
          the Twitter and Polymarket search in the next turn.
        </p>
        {inputs.twitter?.dynamic_keywords?.length > 0 && (
          <div className="engine-pills">
            {inputs.twitter.dynamic_keywords.map(k => (
              <span key={k} className="engine-pill engine-pill--dynamic">{k}</span>
            ))}
          </div>
        )}
      </section>

      {/* ── TURN 1.75 ──────────────────────────────────────────────────── */}
      <section className="engine-section">
        <div className="engine-turn-header">
          <span className="engine-turn-badge">Turn 1.75</span>
          <h2 className="engine-section-title">Analyst Variables — Keyword Extraction</h2>
        </div>
        <p className="engine-how">
          Load analyst notes from the vault. Match filenames to registered analysts.
          Extract keywords using a financial terms dictionary (gamma, CDS, direct lending,
          days to cover, etc.), known tickers, and multi-word entities. Deduplicate against
          constants + dynamic keywords. These keywords widen the search — the note
          content itself is <strong>never</strong> sent to the synthesis model.
        </p>
        {inputs.analyst_notes?.files?.length > 0 && (
          <>
            <h3 className="engine-subsection">Notes Consumed</h3>
            <div className="engine-pills">
              {inputs.analyst_notes.files.map(f => (
                <span key={f} className="engine-pill">{f}</span>
              ))}
            </div>
          </>
        )}
        {inputs.twitter?.analyst_keywords?.length > 0 && (
          <>
            <h3 className="engine-subsection">Extracted Keywords</h3>
            <div className="engine-pills">
              {inputs.twitter.analyst_keywords.map(k => (
                <span key={k} className="engine-pill engine-pill--analyst">{k}</span>
              ))}
            </div>
          </>
        )}
        {(!inputs.analyst_notes?.files || inputs.analyst_notes.files.length === 0) && (
          <p className="engine-empty">No new analyst notes consumed in the latest run.</p>
        )}
      </section>

      {/* ── TURN 2 ─────────────────────────────────────────────────────── */}
      <section className="engine-section">
        <div className="engine-turn-header">
          <span className="engine-turn-badge">Turn 2</span>
          <h2 className="engine-section-title">Variables Pull — Dynamic Data</h2>
        </div>
        <p className="engine-how">
          Now search Twitter and Polymarket using the full keyword set: constants +
          dynamic (from news) + analyst-extracted. This is how analyst notes widen
          the aperture without being injected into the prompt.
        </p>

        <div className="engine-step-grid">
          <div className="engine-step-card">
            <div className="engine-step-name">Twitter Signals</div>
            <div className="engine-step-source">Tweepy, 7-day window</div>
            <p className="engine-step-desc">
              Search recent tweets for all keyword sets. Filter: min 5 engagement
              (likes + retweets), exclude retweets and replies. Deduplicate by tweet ID,
              sort by engagement.
            </p>
            {inputs.twitter && <div className="engine-step-stat">{inputs.twitter.count} tweets</div>}
          </div>

          <div className="engine-step-card">
            <div className="engine-step-name">Polymarket Opportunities</div>
            <div className="engine-step-source">Snapshot + Gamma API</div>
            <p className="engine-step-desc">
              Static snapshot from polymarket-wallet-project (pre-computed EV+ scoring
              via Haiku+Opus pipeline). Plus live Gamma API queries for dynamic keywords
              (max 5 queries). Filter by spread, active status, price range.
            </p>
            {inputs.polymarket && (
              <div className="engine-step-stat">
                {inputs.polymarket.snapshot_count} snapshot + {inputs.polymarket.dynamic_count || 0} dynamic markets
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── TURN 2.5 ───────────────────────────────────────────────────── */}
      <section className="engine-section">
        <div className="engine-turn-header">
          <span className="engine-turn-badge">Turn 2.5</span>
          <h2 className="engine-section-title">Build Source Registry</h2>
        </div>
        <p className="engine-how">
          Assign a stable, citable ID to every data point. Each source gets a
          deterministic ID based on its type and origin. These IDs are embedded
          in the prompt so the model can cite them inline with <code>[src:ID]</code> tags.
        </p>
        <div className="engine-id-examples">
          <div className="engine-id-row"><code>news-{'<article_id>'}</code><span>News articles</span></div>
          <div className="engine-id-row"><code>tweet-{'<tweet_id>'}</code><span>Twitter signals</span></div>
          <div className="engine-id-row"><code>poly-{'<condition_id>'}</code><span>Polymarket markets</span></div>
          <div className="engine-id-row"><code>fred-{'<series_id>'}</code><span>FRED macro series</span></div>
          <div className="engine-id-row"><code>job-{'<industry>-<title>'}</code><span>Job signals</span></div>
          <div className="engine-id-row"><code>bdc-{'<ticker>'}</code><span>BDC/private credit signals</span></div>
        </div>
      </section>

      {/* ── TURN 3 ─────────────────────────────────────────────────────── */}
      <section className="engine-section">
        <div className="engine-turn-header">
          <span className="engine-turn-badge">Turn 3</span>
          <h2 className="engine-section-title">Opus Synthesis</h2>
        </div>
        <p className="engine-how">
          Bundle all data into a single prompt and call Claude Opus. The prompt includes
          every data point with its <code>[src:ID]</code> tag. The model writes the
          Market Daily Brief with inline citations. Every factual claim must cite its source.
          The model also outputs a structured JSON block of named signals with direction
          and conviction.
        </p>

        <h3 className="engine-subsection">Prompt Structure</h3>
        <div className="engine-prompt-sections">
          <div className="engine-prompt-item"><span className="engine-prompt-num">1</span>Live market prices (SPY, QQQ, VIX, CL, GC, DXY, yields)</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">2</span>FRED macro indicators with latest values and dates</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">3</span>Private credit signals (BDC holdings, short interest, stress indicators)</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">4</span>Most-mentioned companies and growing industries from entity extraction</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">5</span>Full news flow with [src:ID] tags ({inputs.news?.count || '~450'}+ articles)</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">6</span>Hiring velocity by industry with sample companies</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">7</span>Twitter signals sorted by engagement with [src:ID] tags</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">8</span>Polymarket opportunities with EV+ scoring and [src:ID] tags</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">9</span>Signal ledger history (what the model said last time and conviction trajectory)</div>
        </div>

        <h3 className="engine-subsection">Output Sections</h3>
        <div className="engine-prompt-sections">
          <div className="engine-prompt-item"><span className="engine-prompt-num">1</span>Market Regime — current macro environment</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">2</span>The Macro Backdrop — economic indicators story</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">3</span>High Conviction Trades — ranked with instrument, direction, thesis, risk, sizing</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">4</span>Sector Intelligence — accelerating vs. contracting sectors from hiring + news</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">5</span>Prediction Market Alpha — Polymarket mispricings with EV+ reasoning</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">6</span>Key Risks to Watch — 3 developments that change everything</div>
          <div className="engine-prompt-item"><span className="engine-prompt-num">7</span>Data Quality Note — strongest signals, stale data, what would sharpen tomorrow</div>
        </div>

        {outputs.source_types && (
          <>
            <h3 className="engine-subsection">Sources Cited (Latest Run)</h3>
            <div className="engine-source-bars">
              {Object.entries(outputs.source_types)
                .sort(([,a], [,b]) => b - a)
                .map(([type, count]) => {
                  const max = Math.max(...Object.values(outputs.source_types));
                  return (
                    <div key={type} className="engine-bar-row">
                      <span className="engine-bar-label">{type.replace(/_/g, ' ')}</span>
                      <div className="engine-bar-track">
                        <div className="engine-bar-fill" style={{ width: `${(count / max) * 100}%` }} />
                      </div>
                      <span className="engine-bar-count">{count}</span>
                    </div>
                  );
                })}
            </div>
            <p className="engine-detail" style={{ marginTop: 8 }}>
              {outputs.sources_cited} total sources cited in the brief.
            </p>
          </>
        )}
      </section>

      {/* ── TURN 4 ─────────────────────────────────────────────────────── */}
      <section className="engine-section">
        <div className="engine-turn-header">
          <span className="engine-turn-badge">Turn 4</span>
          <h2 className="engine-section-title">Output + Signal Ledger Update</h2>
        </div>
        <p className="engine-how">
          Write the thesis as JSON (served to the frontend) and markdown (archived to the vault).
          Extract the structured signals block from the model output and merge it into the
          persistent signal ledger. Signals not seen for {'>'}3 days transition to "fading".
        </p>

        <h3 className="engine-subsection">Files Written</h3>
        <div className="engine-id-examples">
          <div className="engine-id-row"><code>thesis-YYYY-MM-DD.json</code><span>Thesis + sources (served by API)</span></div>
          <div className="engine-id-row"><code>thesis-YYYY-MM-DD.md</code><span>Thesis markdown (archived to vault)</span></div>
          <div className="engine-id-row"><code>thesis-YYYY-MM-DD.manifest.json</code><span>Full input/output manifest for reproducibility</span></div>
          <div className="engine-id-row"><code>signal-ledger.json</code><span>Updated signal tracking (conviction, status, history)</span></div>
        </div>

        <h3 className="engine-subsection">Conviction Tracking</h3>
        <p className="engine-detail">
          Each named signal has a conviction trajectory: <strong>new</strong> on first mention,
          then <strong>strengthening</strong> or <strong>weakening</strong> based on source count
          and direction consistency across runs. Signals not mentioned for {'>'}3 days
          transition to <strong>fading</strong>. Can be manually marked <strong>resolved</strong>.
        </p>
      </section>

      {/* ── SIGNAL LEDGER ──────────────────────────────────────────────── */}
      <section className="engine-section">
        <h2 className="engine-section-title">Signal Ledger</h2>
        <p className="engine-section-desc">Named signals tracked across thesis runs. Conviction builds over time.</p>

        {signals.length === 0 ? (
          <p className="engine-empty">No signals tracked yet. Run the first thesis to populate.</p>
        ) : (
          <table className="engine-table">
            <thead>
              <tr>
                <th>Signal</th>
                <th>Direction</th>
                <th>First Seen</th>
                <th>Last Seen</th>
                <th>Mentions</th>
                <th>Conviction</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {signals.map((sig, i) => {
                const st = STATUS_LABELS[sig.status] || STATUS_LABELS.active;
                return (
                  <tr key={i}>
                    <td className="engine-signal-name">
                      {sig.name}
                      {sig.one_liner && <span className="engine-signal-liner">{sig.one_liner}</span>}
                    </td>
                    <td><span className={`engine-direction engine-direction--${sig.direction}`}>{sig.direction}</span></td>
                    <td className="engine-date">{sig.first_seen}</td>
                    <td className="engine-date">{sig.last_seen}</td>
                    <td>{sig.thesis_mentions?.length || 0}</td>
                    <td><span style={{ color: CONVICTION_COLORS[sig.conviction] || '#686b82' }}>{sig.conviction}</span></td>
                    <td><span className="engine-status" style={{ background: st.bg, color: st.color }}>{sig.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* ── LATEST RUN MANIFEST ────────────────────────────────────────── */}
      {manifest && (
        <section className="engine-section">
          <h2 className="engine-section-title">Latest Run Manifest</h2>
          <p className="engine-section-desc">
            {manifest.thesis_id} &middot; Constants v{manifest.constants_version}
          </p>

          <div className="engine-manifest-grid">
            {inputs && Object.entries(inputs).map(([key, val]) => (
              <div key={key} className="engine-manifest-card">
                <div className="engine-manifest-label">{key.replace(/_/g, ' ')}</div>
                <div className="engine-manifest-value">
                  {typeof val === 'object' ? (val.count ?? val.series_count ?? JSON.stringify(val).slice(0, 60)) : val}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── HISTORY ────────────────────────────────────────────────────── */}
      {history?.length > 0 && (
        <section className="engine-section">
          <h2 className="engine-section-title">Thesis History</h2>
          <div className="engine-history">
            {history.map(h => (
              <Link key={h.id} to="/markets" className="engine-history-item">
                {h.date}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="engine-footer">
        <p>Signal intelligence by <strong>ferl</strong></p>
        <p className="engine-footer-note">
          Constants define the baseline. Analyst notes widen the search.
          The signal ledger tracks conviction. Every claim is cited.
          Every input is logged. Transparent by design.
        </p>
      </footer>
    </div>
  );
}
